/* global jQuery */
/*
 * Addon Variant Picker
 *
 * Binds the "Velg type" trigger button and the cards inside
 * snippets/addon-variant-picker.liquid. Runs in three contexts:
 *
 *   pdp   — writes chosen variant into hidden inputs (#secWarranty-*),
 *           checks the #add-secWarranty checkbox, toggles the trigger
 *           button state, and calls updatePriceAfterAddingCareOrWarranty().
 *
 *   cart  — dispatches `addon-picker:chosen` on document. Cart JS files
 *           (cart-epl.js, cart-drawer-epl.js) listen for this and run the
 *           existing add flow. Modal closes on event dispatch.
 *
 * When trigger button is in "added" state, click re-dispatches to the
 * legacy remove path (unchecking the checkbox in PDP, firing the existing
 * remove handler in cart contexts).
 */
(function () {
  'use strict';

  // The theme includes this script from main-product.liquid, main-cart-items-epl.liquid,
  // and cart-drawer-epl.liquid. Browsers usually dedupe by URL, but pages that render
  // two of those surfaces can still execute the IIFE twice, which would register every
  // click listener twice and dispatch `addon-picker:chosen` twice per pick.
  if (window.__addonVariantPickerInitialized) return;
  window.__addonVariantPickerInitialized = true;

  const SELECTORS = {
    picker: '[data-addon-picker]',
    chooseBtn: '[data-addon-picker-choose]',
    trigger: '[data-addon-picker-trigger]',
    modal: '.ac-modal--addon-picker',
  };

  const PDP = {
    swVaridInput: '#secWarranty-varid',
    swSkuInput: '#secWarranty-product-sku',
    swHandleInput: '#secWarranty-product-handle',
    swMpnInput: '#secWarranty-product-mpn',
    swCheckbox: '#add-secWarranty',
    // Use attribute selector instead of id: main-product.liquid has two SW
    // render branches that both emit #secWarranty-add, so getElementById
    // would only find the first. data-picker-enabled only exists on the
    // active picker trigger.
    swTrigger: '[data-picker-enabled="true"]',
    swTriggerLabel: '.pdp-secWarranty-btn',
    // Visible SW addon component swap targets — populated by
    // updatePdpAddonComponent() to keep the on-page card in sync with the
    // chosen picker entry. Both render branches in main-product.liquid carry
    // these classes so a single set of selectors works regardless of branch.
    swAddonContainer: '.secWarranty-replace',
    swAddonImage: '.js-pdp-sw-image',
    swAddonHeader: '.js-pdp-sw-header',
    swAddonDescription: '.js-pdp-sw-description',
    swAddonPrice: '.js-pdp-sw-price',
  };

  function ensureModalWrapper(modal) {
    // ac-modal.min.js wraps modals in a .modal seedkit-component-standalone
    // container at DOMContentLoaded. Modals rendered later (e.g. into a
    // dynamically-loaded cart drawer) never get that wrapper. Build one
    // that matches the library's exact DOM shape so styling and close
    // behavior work, then move the modal to <body> so it stacks above
    // the cart drawer (drawer z-index is 1000).
    let container = modal.closest('.modal');
    if (container) return container;

    // If a previous drawer render orphaned a wrapper for the same modal id
    // under <body>, remove it so we don't accumulate dead DOM.
    const orphans = document.querySelectorAll(
      'body > .modal.addon-picker-modal-wrapper'
    );
    orphans.forEach((o) => {
      if (o.querySelector('#' + CSS.escape(modal.id))) {
        o.remove();
      }
    });

    const dialogLabel = modal.getAttribute('data-modal-dialog-label') || 'Modal';
    const closeLabel = modal.getAttribute('data-modal-close-label') || 'Close Modal';

    container = document.createElement('div');
    container.className =
      'modal seedkit-component-standalone modal-crossfade modal-curtain-blur ' +
      'modal-wide modal-page-overlay addon-picker-modal-wrapper';
    container.setAttribute('data-modal-element-container', '');
    container.setAttribute('data-modal-close', '');
    container.style.zIndex = '1100';

    const overlayContainer = document.createElement('div');
    overlayContainer.className = 'modal-overlay-container';
    overlayContainer.setAttribute('data-modal-element-overlay-container', '');
    overlayContainer.setAttribute('data-modal-close', '');

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.setAttribute('data-modal-element-overlay', '');
    overlay.setAttribute('data-modal-close-button-parent', '');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('tabindex', '-1');
    overlay.setAttribute('aria-label', dialogLabel);
    overlay.setAttribute('aria-hidden', 'true');

    const contentContainer = document.createElement('div');
    contentContainer.className = 'modal-content-container';
    contentContainer.setAttribute('data-modal-element-content-container', '');

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'modal-close-button';
    closeBtn.setAttribute('data-modal-element-close-button', '');
    closeBtn.setAttribute('data-modal-close', '');
    closeBtn.setAttribute('aria-label', closeLabel);

    const closeIcon = document.createElement('span');
    closeIcon.className = 'modal-close-icon';
    closeIcon.setAttribute('data-modal-element-close-icon', '');
    closeIcon.setAttribute('data-modal-close', '');
    closeIcon.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">' +
      '<path d="M12.12,10l4.07-4.06a1.5,1.5,0,1,0-2.11-2.12L10,7.88,5.94,3.81' +
      'A1.5,1.5,0,1,0,3.82,5.93L7.88,10,3.81,14.06a1.5,1.5,0,0,0,0,2.12,' +
      '1.51,1.51,0,0,0,2.13,0L10,12.12l4.06,4.07a1.45,1.45,0,0,0,1.06.44,' +
      '1.5,1.5,0,0,0,1.06-2.56Z"></path></svg>';
    closeBtn.appendChild(closeIcon);

    overlay.appendChild(contentContainer);
    overlay.appendChild(closeBtn);
    overlayContainer.appendChild(overlay);
    container.appendChild(overlayContainer);
    contentContainer.appendChild(modal);
    document.body.appendChild(container);
    return container;
  }

  function openModal(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return false;
    const container = ensureModalWrapper(modal);
    if (!container) return false;
    container.classList.add('modal-open');
    document.documentElement.classList.add('has-modal');
    // Reflect dialog state on the trigger that owns this modal.
    const triggerForAria = document.querySelector(
      '[aria-controls="' + modalId + '"]'
    );
    if (triggerForAria) triggerForAria.setAttribute('aria-expanded', 'true');
    if (!container._pickerCloseBound) {
      container._pickerCloseBound = true;
      container.addEventListener('click', (e) => {
        // Keep content interactive — only close on backdrop/overlay or close button.
        if (e.target.closest('.modal-content-container')) return;
        container.classList.remove('modal-open');
        const anyStillOpen = document.querySelector('.modal.modal-open');
        if (!anyStillOpen) {
          document.documentElement.classList.remove('has-modal', 'has-modal-page-overlay');
        }
      });
    }
    // Move focus into the modal for a11y. Prefer the close button; fall back
    // to the first choose CTA.
    const focusTarget =
      container.querySelector('.modal-close-button') ||
      container.querySelector('[data-addon-picker-choose]:not([disabled])');
    if (focusTarget && typeof focusTarget.focus === 'function') {
      // Defer one tick so the container is painted before focus moves.
      window.requestAnimationFrame(() => focusTarget.focus());
    }
    return true;
  }

  function closeModal(pickerEl) {
    const modal = pickerEl.closest(SELECTORS.modal);
    if (!modal) return;
    const container = modal.closest('.modal');
    // Reset aria-expanded on the owning trigger. Intentionally do NOT call
    // .focus() here: the theme's .pdp-secWarranty-btn:focus rule applies the
    // hover style (underline + color), which looks like a stuck hover after
    // a click-close. Keyboard users get explicit focus handling via
    // onEscapeKey. Mouse users don't need focus restoration.
    const triggerForAria = document.querySelector(
      '[aria-controls="' + modal.id + '"]'
    );
    if (triggerForAria) {
      triggerForAria.setAttribute('aria-expanded', 'false');
    }
    if (container) {
      container.classList.remove('modal-open');
      const anyStillOpen = document.querySelector('.modal.modal-open');
      if (!anyStillOpen) {
        document.documentElement.classList.remove('has-modal', 'has-modal-page-overlay');
      }
      return;
    }
    modal.classList.remove('is-open', 'ac-modal--open');
  }

  // Delegate listener for picker triggers. Runs in capture so it fires
  // even if ac-modal-custom hasn't wired the trigger (which happens for
  // modals rendered inside dynamically-updated cart drawers).
  function onPickerTriggerClick(event) {
    const trigger = event.target.closest('[data-addon-picker-trigger="true"]');
    if (!trigger) return;
    const modalId = trigger.dataset.modalTarget;
    if (!modalId) return;
    // If ac-modal-custom already wired this trigger, let it handle the open.
    if (trigger._acModalInitialized) return;
    event.preventDefault();
    event.stopPropagation();
    openModal(modalId);
  }

  function setTriggerAdded(isAdded) {
    const trigger = document.querySelector(PDP.swTrigger);
    if (!trigger) return;
    trigger.dataset.pickerAdded = isAdded ? 'true' : 'false';
    trigger.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
    // Support both shapes: text on a nested label, or on the trigger itself.
    const label = trigger.querySelector(PDP.swTriggerLabel) || trigger;
    const addedText = trigger.dataset.addedText;
    const choosingText = trigger.dataset.choosingText;
    if (isAdded && addedText) label.textContent = addedText;
    if (!isAdded && choosingText) label.textContent = choosingText;
  }

  function setVal(selector, value) {
    const el = document.querySelector(selector);
    if (el) el.value = value == null ? '' : value;
  }

  // Parse a Shopify-formatted money string like "5 290 kr" or "5 290,00 kr"
  // into whole kroner. Strips the fractional part and all non-digit chars.
  function parseMoneyToKroner(str) {
    if (!str) return 0;
    const normalized = String(str).replace(/ /g, ' ');
    // Drop decimal portion (comma or dot + trailing digits) before stripping.
    const withoutDecimals = normalized.replace(/[.,]\d+(?!\d)/, '');
    const digitsOnly = withoutDecimals.replace(/[^\d]/g, '');
    return parseInt(digitsOnly, 10) || 0;
  }

  function formatKroner(kroner) {
    const locale = document.documentElement.lang || 'nb-NO';
    try {
      return kroner.toLocaleString(locale).replace(/ /g, ' ') + ' kr';
    } catch (e) {
      return kroner + ' kr';
    }
  }

  // Rewrite the bundled-price data-attrs on .js-product-price-with-care-warranty
  // elements using the chosen SW variant's price. The legacy Liquid freezes these
  // attrs at render time based on whichever entry the Liquid picked first, so
  // without this the displayed bundle price wouldn't change across entries.
  // AC+ contribution is derived from the delta of data-price-with-care minus
  // data-price, keeping us from needing to know the AC price separately.
  function updatePdpBundledPriceAttrs(swPriceCents) {
    if (!swPriceCents) return;
    const swKroner = Math.round(swPriceCents / 100);
    const elements = document.querySelectorAll('.js-product-price-with-care-warranty');
    elements.forEach((el) => {
      const baseKr = parseMoneyToKroner(el.getAttribute('data-price'));
      if (!baseKr) return;
      const priceWithCareKr = parseMoneyToKroner(el.getAttribute('data-price-with-care'));
      const careKr = priceWithCareKr > baseKr ? priceWithCareKr - baseKr : 0;
      el.setAttribute('data-price-with-secWarranty', formatKroner(baseKr + swKroner));
      if (careKr > 0) {
        el.setAttribute(
          'data-price-with-care-and-secWarranty',
          formatKroner(baseKr + careKr + swKroner)
        );
      }
    });
  }

  // Mirror the chosen picker entry's content into the visible SW addon
  // component on the PDP. Liquid renders entry[0] by default, so without this
  // step the picker would silently update hidden inputs while the on-page
  // image, header, description, and price kept showing the first entry.
  function updatePdpAddonComponent(btn) {
    const container = document.querySelector(PDP.swAddonContainer);
    if (!container) return;

    const imgEl = container.querySelector(PDP.swAddonImage);
    if (imgEl && btn.dataset.componentImage) {
      imgEl.src = btn.dataset.componentImage;
    }

    const headerEl = container.querySelector(PDP.swAddonHeader);
    if (headerEl && btn.dataset.componentHeader != null) {
      headerEl.textContent = btn.dataset.componentHeader;
    }

    const descEl = container.querySelector(PDP.swAddonDescription);
    if (descEl && btn.dataset.componentDescription != null) {
      // Description is rich-text HTML. Liquid emits it via |escape into the
      // data attr, the browser unescapes on dataset read, so .innerHTML here
      // assigns the original markup back into the DOM.
      descEl.innerHTML = btn.dataset.componentDescription;
    }

    const priceEl = container.querySelector(PDP.swAddonPrice);
    if (priceEl && btn.dataset.componentPriceText) {
      priceEl.textContent = btn.dataset.componentPriceText;
    }
  }

  function handlePdpChoose(btn) {
    const variantId = btn.dataset.variantId;
    const sku = btn.dataset.productSku;
    const handle = btn.dataset.productHandle;
    const mpn = btn.dataset.productMpn;
    const swPriceCents = parseInt(btn.dataset.productPrice, 10) || 0;

    setVal(PDP.swVaridInput, variantId);
    setVal(PDP.swSkuInput, sku);
    setVal(PDP.swHandleInput, handle);
    setVal(PDP.swMpnInput, mpn);

    const checkbox = document.querySelector(PDP.swCheckbox);
    if (checkbox) {
      checkbox.checked = true;
      checkbox.value = variantId;
    }

    setTriggerAdded(true);
    updatePdpBundledPriceAttrs(swPriceCents);
    updatePdpAddonComponent(btn);

    if (typeof window.updatePriceAfterAddingCareOrWarranty === 'function') {
      window.updatePriceAfterAddingCareOrWarranty();
    } else if (typeof jQuery !== 'undefined') {
      // Legacy handler is defined inside a jQuery document.ready closure.
      // Fall back to manually triggering the trigger's click path by
      // dispatching a custom event the page listens to.
      jQuery(document).trigger('sw:picker-chose', { variantId: variantId });
    }
  }

  function handleCartChoose(btn, pickerEl) {
    // Resolve which visible trigger opened this modal — both the cart page
    // and the cart drawer can render a picker trigger for the same item, and
    // both bridges (cart-epl.js + cart-drawer-epl.js) listen on document.
    // Including the trigger element lets each bridge self-filter by
    // containment and avoid double-dispatch.
    const modal = btn.closest('.ac-modal--addon-picker');
    const triggerEl = modal
      ? document.querySelector('[aria-controls="' + modal.id + '"]')
      : null;

    const detail = {
      variantId: btn.dataset.variantId,
      productSku: btn.dataset.productSku,
      productHandle: btn.dataset.productHandle,
      productMpn: btn.dataset.productMpn,
      productPrice: btn.dataset.productPrice,
      mainItemKey: pickerEl.dataset.mainItemKey,
      mainProductSku: pickerEl.dataset.mainProductSku,
      mainProductHandle: pickerEl.dataset.mainProductHandle,
      mainProductUuid: pickerEl.dataset.mainProductUuid,
      sectionId: pickerEl.dataset.sectionId,
      triggerElement: triggerEl,
    };
    document.dispatchEvent(new CustomEvent('addon-picker:chosen', { detail: detail, bubbles: true }));
  }

  function onChooseClick(event) {
    const btn = event.target.closest(SELECTORS.chooseBtn);
    if (!btn || btn.disabled) return;

    const pickerEl = btn.closest(SELECTORS.picker);
    if (!pickerEl) return;

    event.preventDefault();

    const context = pickerEl.dataset.context || 'pdp';
    if (context === 'pdp') {
      handlePdpChoose(btn);
    } else {
      handleCartChoose(btn, pickerEl);
    }

    closeModal(pickerEl);
  }

  // When the PDP trigger is in "added" state, clicking it should remove
  // the selection instead of reopening the picker. Intercept the click on
  // capture so we run before the ac-modal-trigger's own open handler.
  function onTriggerClick(event) {
    const trigger = event.target.closest(PDP.swTrigger);
    if (!trigger) return;

    const isAdded = trigger.dataset.pickerAdded === 'true';
    if (!isAdded) {
      // Let ac-modal open the picker. Do nothing.
      return;
    }

    // Already added — user wants to remove. Stop the modal from opening.
    event.preventDefault();
    event.stopPropagation();

    setVal(PDP.swVaridInput, '');
    setVal(PDP.swSkuInput, '');
    setVal(PDP.swHandleInput, '');
    setVal(PDP.swMpnInput, '');

    const checkbox = document.querySelector(PDP.swCheckbox);
    if (checkbox) checkbox.checked = false;

    setTriggerAdded(false);

    // Revert the visible addon component to entry[0]. The first card in the
    // owning picker modal represents the default Liquid render, so its data
    // attrs are the source of truth for the reset state.
    const modalId = trigger.dataset.modalTarget;
    const modal = modalId ? document.getElementById(modalId) : null;
    const firstCardBtn = modal
      ? modal.querySelector('[data-addon-picker-choose]')
      : null;
    if (firstCardBtn) updatePdpAddonComponent(firstCardBtn);

    if (typeof window.updatePriceAfterAddingCareOrWarranty === 'function') {
      window.updatePriceAfterAddingCareOrWarranty();
    }
  }

  // Flip aria-expanded on any picker trigger click, regardless of which
  // handler ends up opening the modal. Paired with a MutationObserver below
  // that resets expanded when .modal-open is removed from the container.
  // Also blur pointer-opened triggers so the theme's :focus-matches-hover
  // style doesn't leave a "stuck hover" appearance after the modal closes.
  function onAnyTriggerAriaUpdate(event) {
    const trigger = event.target.closest('[aria-haspopup="dialog"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', 'true');
    // event.detail === 0 means keyboard-activated (Enter/Space); keep focus
    // on the trigger in that case so Escape can return it naturally.
    if (event.detail && typeof trigger.blur === 'function') {
      trigger.blur();
    }
  }

  // Chromium holds onto a "stuck hover" on the element that was under the
  // cursor when an overlay appeared, even after the overlay is removed.
  // Toggling pointer-events via rAF forces the browser to re-hit-test and
  // release the hover state. Without this the picker trigger keeps its
  // hover style (underline + color) until the user clicks somewhere else.
  function resetStuckHover(el) {
    if (!el || !el.style) return;
    // Guard against re-entry: the MutationObserver that invokes this can
    // fire multiple times during a close (each class-list mutation). Without
    // the flag the second call captures prev="none" from the first call's
    // in-flight state, and the restore leaves pointer-events stuck at none.
    if (el._pickerHoverResetPending) return;
    el._pickerHoverResetPending = true;
    const prev = el.style.pointerEvents;
    el.style.pointerEvents = 'none';
    // setTimeout rather than rAF because rAFs are throttled/suspended in
    // unfocused or background tabs, which could leave pointer-events stuck.
    window.setTimeout(() => {
      el.style.pointerEvents = prev;
      el._pickerHoverResetPending = false;
    }, 50);
  }

  function observeModalClose() {
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== 'attributes' || m.attributeName !== 'class') continue;
        const target = m.target;
        if (!target.classList || !target.classList.contains('modal')) continue;
        // Modal just closed — reset aria-expanded on the linked trigger
        // and nudge the browser to drop any stuck hover styling.
        if (!target.classList.contains('modal-open')) {
          const innerModal = target.querySelector('[id^="modal-"]');
          if (!innerModal) continue;
          // Only act on picker modals; other modals may not need this treatment.
          if (!innerModal.classList.contains('ac-modal--addon-picker')) continue;
          const trigger = document.querySelector(
            '[aria-controls="' + innerModal.id + '"]'
          );
          if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
            resetStuckHover(trigger);
          }
        }
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  function onEscapeKey(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    // Iterate all open modals and close the topmost picker — taking only
    // the first `.modal.modal-open` could miss us if another (non-picker)
    // modal sits earlier in DOM order.
    const openContainers = document.querySelectorAll('.modal.modal-open');
    let pickerContainer = null;
    openContainers.forEach((c) => {
      if (c.querySelector('.ac-modal--addon-picker')) pickerContainer = c;
    });
    if (!pickerContainer) return;
    pickerContainer.classList.remove('modal-open');
    // Only strip the global has-modal classes when no other modal is open.
    const anyStillOpen = document.querySelector('.modal.modal-open');
    if (!anyStillOpen) {
      document.documentElement.classList.remove('has-modal', 'has-modal-page-overlay');
    }
    // Restore focus to the trigger that owned this modal.
    const innerModal = pickerContainer.querySelector('[id^="modal-"]');
    if (innerModal) {
      const trigger = document.querySelector('[aria-controls="' + innerModal.id + '"]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        if (typeof trigger.focus === 'function') trigger.focus();
      }
    }
  }

  function init() {
    document.addEventListener('click', onChooseClick);
    document.addEventListener('click', onTriggerClick, true);
    document.addEventListener('click', onPickerTriggerClick, true);
    document.addEventListener('click', onAnyTriggerAriaUpdate);
    document.addEventListener('keydown', onEscapeKey);
    observeModalClose();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
